import type {
  DeliveryState,
  FleetActivityAgent,
  FleetActivityItem,
  FleetActivitySnapshot,
} from '../../api/types';

export function agentKey(agent: { tenant_id: string; alias: string }): string {
  return `${agent.tenant_id}/${agent.alias}`;
}

/**
 * Extracts the alias from a tenant/alias key. If the key does not contain '/', it returns the whole key.
 */
export function aliasDe(key: string): string {
  const corte = key.indexOf('/');
  return corte === -1 ? key : key.slice(corte + 1);
}

export function plural(count: number, one: string, many: string): string {
  return `${String(count)} ${count === 1 ? one : many}`;
}

export function humanSeconds(seconds: number): string {
  if (seconds < 60) return `${String(Math.round(seconds))} s`;
  if (seconds < 3600) return `${String(Math.floor(seconds / 60))} min`;
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return minutes === 0 ? `${String(hours)} h` : `${String(hours)} h ${String(minutes)} min`;
}

/** Live delegation edge: `from` passed a delivery to `to`, and `to` has it in flight NOW. */
export interface DelegationEdge {
  from: string;
  to: string;
  deliveryId?: string | null;
  lane?: string | null;
  secondsInFlight?: number | null;
}

/**
 * Who passed work to whom, read from the in-flight deliveries. `in_flight_items[].from_alias` is the
 * sender of the delivery the agent is processing, so a delivery that `b` has in flight and sent `a` IS
 * the edge `a -> b`.
 *
 * Two cases that are not delegation between agents are discarded:
 *  - `from` equal to `to`: the Telegram bridge publishes the owner's message with the agent's own
 *    alias, and that is a person typing, not a delegation.
 *  - `from` that is no alias of the fleet: it comes from outside (a person, a channel).
 */
export function delegationEdges(snapshot: FleetActivitySnapshot | undefined): DelegationEdge[] {
  const agents = snapshot?.agents ?? [];
  const known = new Set(agents.map(agentKey));
  const edges: DelegationEdge[] = [];
  for (const agent of agents) {
    const to = agentKey(agent);
    for (const item of agent.in_flight_items ?? []) {
      if (!item.from_tenant || !item.from_alias) continue;
      const from = `${item.from_tenant}/${item.from_alias}`;
      if (from === to || !known.has(from)) continue;
      edges.push({
        from,
        to,
        deliveryId: item.delivery_id,
        lane: item.lane,
        secondsInFlight: item.seconds_in_flight,
      });
    }
  }
  return edges;
}

/**
 * What is remembered about ONE in-flight delivery, to be able to say something when it disappears.
 *
 * The `ack_deadline_at` that was last seen attests to a verifiable fact: if it had already passed,
 * that delivery did NOT leave flight through a clean close.
 */
export interface ItemMemory {
  deliveryId: string;
  /** Last seen state. Always one of leased/accepted/started: that is all the SQL lists. */
  status?: DeliveryState | null;
  /** `ack_deadline_at` in ms, or `null` if the server did not report it. Never filled with 0. */
  ackDeadlineMs: number | null;
}

/** What needs to be remembered from a snapshot to detect transitions in the next one. */
export interface AgentMemory {
  items: ItemMemory[];
  queued: number;
  observedAtMs: number;
}

export type FleetMemory = Record<string, AgentMemory | undefined>;

function itemMemories(items: readonly FleetActivityItem[] | null | undefined): ItemMemory[] {
  const salida: ItemMemory[] = [];
  for (const item of items ?? []) {
    if (typeof item.delivery_id !== 'string') continue;
    const deadline = typeof item.ack_deadline_at === 'string' ? Date.parse(item.ack_deadline_at) : Number.NaN;
    salida.push({
      deliveryId: item.delivery_id,
      status: item.status,
      ackDeadlineMs: Number.isFinite(deadline) ? deadline : null,
    });
  }
  return salida;
}

export function rememberFleet(snapshot: FleetActivitySnapshot | undefined, nowMs: number): FleetMemory {
  const memory: FleetMemory = {};
  for (const agent of snapshot?.agents ?? []) {
    memory[agentKey(agent)] = {
      items: itemMemories(agent.in_flight_items),
      queued: agent.queued ?? 0,
      observedAtMs: nowMs,
    };
  }
  return memory;
}

/**
 * What is known about the outcome of a delivery that left flight.
 * `desconocido` by default; `deadline_vencido` when ack_deadline_at had already passed.
 */
export type PulseOutcome = 'desconocido' | 'deadline_vencido';

export interface Pulse {
  kind: 'received' | 'settled';
  atMs: number;
  deliveryId?: string;
  /** Only in `settled`. */
  outcome?: PulseOutcome;
}

export type PulseMap = Record<string, Pulse[]>;

/**
 * Compares two snapshots and returns the new pulses. A `delivery_id` that appears is work that CAME IN;
 * one that disappears is a delivery that stopped being in flight.
 */
export function detectPulses(
  previous: FleetMemory,
  snapshot: FleetActivitySnapshot | undefined,
  nowMs: number,
): PulseMap {
  const pulses: PulseMap = {};
  for (const agent of snapshot?.agents ?? []) {
    const key = agentKey(agent);
    const before = previous[key];
    if (!before) continue;
    const now = itemMemories(agent.in_flight_items).map((item) => item.deliveryId);
    const beforeSet = new Set(before.items.map((item) => item.deliveryId));
    const nowSet = new Set(now);
    const emitted: Pulse[] = [];
    for (const id of now) {
      if (!beforeSet.has(id)) emitted.push({ kind: 'received', atMs: nowMs, deliveryId: id });
    }
    for (const item of before.items) {
      if (nowSet.has(item.deliveryId)) continue;
      const vencido = item.ackDeadlineMs !== null && item.ackDeadlineMs <= nowMs;
      emitted.push({
        kind: 'settled',
        atMs: nowMs,
        deliveryId: item.deliveryId,
        outcome: vencido ? 'deadline_vencido' : 'desconocido',
      });
    }
    if (emitted.length === 0 && (agent.queued ?? 0) > before.queued) {
      emitted.push({ kind: 'received', atMs: nowMs });
    }
    if (emitted.length > 0) pulses[key] = emitted;
  }
  return pulses;
}

// ----------------------------------------------------------------------------------------------
// The work that comes in through a human bridge.
// ----------------------------------------------------------------------------------------------

export type OrigenEncargo =
  | { tipo: 'agente'; tenant: string | null; alias: string }
  | { tipo: 'puente'; adapter: string }
  | { tipo: 'actor'; tenant: string | null; alias: string }
  | { tipo: 'desconocido' };

/**
 * The sender governs `origin_adapter`, ALWAYS.
 */
export function origenDeItem(
  item: FleetActivityItem,
  contexto: { selfKey: string; known: ReadonlySet<string> },
): OrigenEncargo {
  const tenant = item.from_tenant ?? null;
  const alias = item.from_alias ?? null;
  const emisor = tenant && alias ? `${tenant}/${alias}` : null;

  if (emisor !== null && alias !== null && emisor !== contexto.selfKey && contexto.known.has(emisor)) {
    return { tipo: 'agente', tenant, alias };
  }

  const adapter = item.origin_adapter;
  if (typeof adapter === 'string' && adapter.length > 0 && adapter !== 'bus') {
    return { tipo: 'puente', adapter };
  }

  if (alias !== null && emisor !== contexto.selfKey) return { tipo: 'actor', tenant, alias };

  return { tipo: 'desconocido' };
}

/** The origins of an agent's in-flight deliveries, in the SAME order as `in_flight_items`. */
export function origenesDeAgente(
  agent: FleetActivityAgent,
  known: ReadonlySet<string>,
): OrigenEncargo[] {
  const selfKey = agentKey(agent);
  return (agent.in_flight_items ?? []).map((item) => origenDeItem(item, { selfKey, known }));
}
